import React, { useEffect, useMemo } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import A5Report from "@/containers/Dashboard/Providers/A5Report";
import A5ScreeningDetails from "@/components/Common/A5ScreeningDetails";
import A5ScreeningList from "@/components/Common/A5ScreeningList";
import { ActionSection } from "@/components/Navigation/Toolbar/ActionSection/ActionSection";
import AddAppointment from "@/components/Provider/ScheduleAvailability/AddAppointment";
import { AdvancedCareActivityNewForm } from "@/components/Provider/PatientView/MemberProfile/AdvancedCareActivity/AdvancedCareActivityNewForm";
import AVCheck from "@/components/Common/AVCheck";
import { canCreateAdvancedCareActivityNotes } from "@/features/acp/helpers/note-creation";
import CareCommunicationForm from "@/components/Provider/PatientView/MemberProfile/CareCommunication/CareCommunicationForm";
import { CareManagementNewForm } from "@/components/Provider/PatientView/MemberProfile/CareManagement/CareManagementNewForm";
import CareTeamList from "@/containers/Dashboard/Providers/PatientProfileView/CareTeamList";
import CaseManagementForm from "@/components/Provider/PatientView/MemberProfile/CaseManagement/CaseManagementForm";
import ChangePassword from "@/components/Authentication/ChangePassword";
import ClinicalAssessment from "@/containers/Dashboard/Providers/PatientProfileView/ClinicalAssessment";
import DischargeData from "@/components/Provider/PatientView/MemberProfile/DischargeData";
import { DraftNotes } from "@/portals/(shared)/(draft-notes)/DraftNotes";
import EditEvent from "@/components/Provider/ScheduleAvailability/EditEvent";
import FileList from "@/containers/Dashboard/Providers/FileList";
import { Footer } from "@/component-library/Footer";
import { FullNoteView } from "@/features/notes/(note-views)/FullNoteView";
import GAD7 from "@/features/assessments/GAD7";
import { getProviderProfile } from "@/redux/actions/auth";
import IBARReport from "@/containers/Dashboard/Providers/IBARReport";
import IPSRReport from "@/containers/Dashboard/Providers/IPSRReport";
import { Main2 } from "@/components/layout/Main";
import mapLabelToComponent from "@/containers/Dashboard/common/mapLabelToComponent";
import { MemberProfile } from "@/features/member-profile/MemberProfile";
import MessagesView from "@/components/Common/MessagesView/MessagesView";
import NewNote from "@/features/notes/NewNote";
import NewNoteV2 from "@/features/notes/NewNoteV2";
import NewReport from "@/containers/Dashboard/Providers/NewReport";
import OBARReport from "@/containers/Dashboard/Providers/OBARReport";
import OnBehalfOfSchedule from "@/containers/Dashboard/Providers/OnBehalfOfSchedule";
import OPSRReport from "@/containers/Dashboard/Providers/OPSRReport";
import Patient from "@/containers/Dashboard/Providers/Patient";
import PatientsList from "@/containers/Dashboard/Providers/PatientsList/PatientsList";
import PHQ9 from "@/features/assessments/PHQ9";
import { PrintNoteView } from "@/features/notes/(note-views)/PrintNoteView";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import ProgressNoteList from "@/containers/Dashboard/Providers/PatientProfileView/ProgressNoteList";
import { ProtectedRoute } from "@/features/access-control/components/ProtectedRoute";
import {
  provider_behavioral,
  provider_case_manager,
  provider_pcp,
  provider_peer,
  providerRoute,
} from "@/config/roles";
import Ratings from "@/containers/Dashboard/Providers/PatientProfileView/Ratings";
import { redirectOnSignIn } from "@/utils/redirectOnSignIn";
import ReferralView from "@/containers/Dashboard/Providers/ReferralView";
import Reports from "@/containers/Dashboard/Providers/Reports";
import SchedulePageV2 from "@/containers/Dashboard/Providers/SchedulePageV2";
import {
  selectProfileEmail,
  selectProviderProfile,
  selectRole,
} from "@/redux/selectors/authSelectors";
import Settings from "@/containers/Dashboard/common/SettingsPage";
import SystemCheck from "@/components/Common/SystemCheck";
import { Toolbar } from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";
import VideoClient from "@/features/video-client/VideoClient";
import { ViewDraftNote } from "@/features/notes/(note-views)/DraftNoteView";
import ViewDraftNoteV2 from "@/features/notes/(note-views)/DraftNoteViewV2";
import ViewNote from "@/features/notes/ViewNote";
import { WaitlistForm } from "@/components/Common/WaitlistForm/WaitlistForm";

import { mapRouteOptions } from "./helpers/route-options";
import { ProviderHome } from "./(home)/ProviderHome";
import { ProviderProfileRouter } from "./(profile)/ProviderProfileRouter";

export function ProviderRouter({ url }: { url: string }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const profile = useSelector(selectProviderProfile);
  const role = useSelector(selectRole);
  const email = useSelector(selectProfileEmail);

  const isCaseManagerOrPeer = role === provider_case_manager || role === provider_peer;
  const isPcp = role === provider_pcp;
  const isPeer = role === provider_peer;
  const isBehavioral = role === provider_behavioral;

  const baseRedirect = isCaseManagerOrPeer ? `${providerRoute}/patient` : `${providerRoute}/home`;

  useEffect(() => {
    redirectOnSignIn(history);
    dispatch(getProviderProfile());
  }, [dispatch, history]);

  const navbarConfiguration = useMemo(() => {
    const routeOptions = mapRouteOptions(url, isPcp, isCaseManagerOrPeer);

    if (profile?.omnisearch_enabled && !routeOptions.some((o) => o.label === "Search")) {
      routeOptions.push({
        activeIconComponent: mapLabelToComponent("Search", true),
        iconComponent: mapLabelToComponent("Search"),
        label: "Search",
      } as any);
    }

    return {
      customRenderSection: [
        {
          componentProps: {
            config: {
              options: [] as unknown[],
            },
          },
          isComponent: true,
          render: ActionSection,
        },
      ],
      routeSection: {
        options: routeOptions,
      },
      selfSection: {},
    };
  }, [profile, url, isPcp, isCaseManagerOrPeer]);

  const providerType = profile?.provider_type;
  const isBehavioralIntake = isBehavioral && providerType === "BEHAVIORAL_INTAKE";
  const showChangePassword = profile && !profile.idp;

  return (
    <>
      <Toolbar
        baseRoute={providerRoute}
        isMessagingEnabled={!isPcp}
        navbarConfiguration={navbarConfiguration}
      />

      <Main2>
        {/* CHANGING ROUTE ORDER CAN LEAD TO UNFORESEEN BEHAVIOR AND NEEDS TO BE TESTED */}
        <Switch>
          {!isCaseManagerOrPeer && <Route path={url + "/home"} render={() => <ProviderHome />} />}

          <Route component={Patient} path={url + "/patient/add"} />

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/add/v2"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <NewNoteV2 />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/add"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            {(props) => <NewNote {...props} />}
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/:noteId/v2/draft"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <ViewDraftNoteV2 />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/:noteId/draft"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            {(props) => <ViewDraftNote {...props} />}
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/:noteId/v2"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <FullNoteView />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/export_notes"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <PrintNoteView />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note/:noteId"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            {(props) => <ViewNote {...props} />}
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/note"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            {(props) => <ProgressNoteList {...props} />}
          </ProtectedRoute>

          {!isPeer && (
            <ProtectedRoute
              fallbackRoute={baseRedirect}
              path={url + "/patient/:patientId/care_communication"}
              permissionKey="patientDiagnosis"
              predicateContext={{ email }}
            >
              <CareCommunicationForm />
            </ProtectedRoute>
          )}

          {!isPeer && (
            <ProtectedRoute
              fallbackRoute={baseRedirect}
              path={url + "/patient/:patientId/case_management"}
              permissionKey="patientDiagnosis"
              predicateContext={{ email }}
            >
              <CaseManagementForm />
            </ProtectedRoute>
          )}

          {!isPeer && (
            <ProtectedRoute
              fallbackRoute={baseRedirect}
              path={url + "/patient/:patientId/care_management"}
              permissionKey="patientDiagnosis"
              predicateContext={{ email }}
            >
              <CareManagementNewForm />
            </ProtectedRoute>
          )}

          {canCreateAdvancedCareActivityNotes(providerType as any) && (
            <Route
              component={AdvancedCareActivityNewForm}
              path={url + "/patient/:patientId/advanced_care_activity"}
            />
          )}

          <Route
            component={ClinicalAssessment}
            path={url + "/patient/:patientId/clinical_assessment"}
          />

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/profile/v2"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <MemberProfile />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/patient/:patientId/care_team"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            <CareTeamList />
          </ProtectedRoute>

          <Route component={IBARReport} path={url + "/patient/:patientId/report/ibar"} />
          <Route component={IPSRReport} path={url + "/patient/:patientId/report/ipsr"} />
          <Route component={OBARReport} path={url + "/patient/:patientId/report/obar"} />
          <Route component={OPSRReport} path={url + "/patient/:patientId/report/opsr"} />
          <Route component={A5Report} path={url + "/patient/:patientId/report/a5"} />
          <Route component={Reports} path={url + "/patient/:patientId/report"} />

          <Route component={Ratings} path={url + "/patient/:patientId/ratings"} />
          <Route component={PHQ9} path={url + "/patient/:patientId/phq-9"} />
          <Route component={GAD7} path={url + "/patient/:patientId/gad-7"} />

          <Route
            path={url + "/patient/:patientId/file"}
            render={(props) => <FileList {...props} type="patient" />}
          />

          <Route
            component={DischargeData}
            path={url + "/patient/:patientId/discharge/:dischargeId"}
          />

          {isBehavioralIntake && (
            <Route component={WaitlistForm} path={url + "/patient/:patientId/waitlist_form"} />
          )}

          <Route component={Patient} path={url + "/patient/:patientId"} />
          <Route component={PatientsList} path={url + "/patient"} />

          <Route component={ProviderProfileRouter} path={url + "/profile"} />

          {!isCaseManagerOrPeer && <Route component={EditEvent} path={url + "/schedule/event"} />}

          {!isCaseManagerOrPeer && (
            <Route component={AddAppointment} path={url + "/schedule/add"} />
          )}

          {isCaseManagerOrPeer ? (
            <Route
              path={url + "/schedule/:providerId"}
              render={(props) => <SchedulePageV2 {...props} />}
            />
          ) : (
            <Route component={OnBehalfOfSchedule} path={url + "/schedule/:providerId"} />
          )}

          {!isCaseManagerOrPeer && (
            <Route path={url + "/schedule"} render={(props) => <SchedulePageV2 {...props} />} />
          )}

          {showChangePassword && (
            <Route
              path={url + "/change-password"}
              render={(props) => <ChangePassword {...props} baseRoute={providerRoute} />}
            />
          )}

          <Route path={url + "/settings"} render={() => <Settings baseRoute={providerRoute} />} />

          <Route
            path={url + "/privacy-policy"}
            render={() => <PrivacyPolicy baseRoute={providerRoute} />}
          />

          <Route
            path={url + "/tutorial"}
            render={() => <TutorialScreens baseRoute={providerRoute} />}
          />

          <Route component={SystemCheck} path={url + "/system-check"} />
          <Route component={AVCheck} path={url + "/av-check"} />
          <Route component={VideoClient} path={url + "/video-call"} />

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/reports/add"}
            permissionKey="reports"
            predicateContext={{ email }}
          >
            <NewReport />
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/reports"}
            permissionKey="reports"
            predicateContext={{ email }}
          >
            {(props) => <Reports {...props} />}
          </ProtectedRoute>

          {!isPcp && <Route component={MessagesView} path={url + "/messages"} />}

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/a5_screenings/:screening_id/file"}
            permissionKey="screening"
            predicateContext={{ email }}
          >
            {(props) => <FileList {...props} type="a5_screenings" />}
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/a5_screenings/:screening_id"}
            permissionKey="screening"
            predicateContext={{ email }}
          >
            {(props) => <A5ScreeningDetails {...props} />}
          </ProtectedRoute>

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/a5_screenings"}
            permissionKey="screening"
            predicateContext={{ email }}
          >
            {(props) => <A5ScreeningList {...props} />}
          </ProtectedRoute>

          <Route
            path={url + "/referral/:referralCode/file"}
            render={(props) => <FileList {...props} type="referral" />}
          />

          <Route component={ReferralView} path={url + "/referral/:referralCode"} />

          <ProtectedRoute
            fallbackRoute={baseRedirect}
            path={url + "/draft-notes"}
            permissionKey="patientDiagnosis"
            predicateContext={{ email }}
          >
            {() => <DraftNotes />}
          </ProtectedRoute>

          <Redirect from={url} to={baseRedirect} />
        </Switch>
      </Main2>

      <Footer />
    </>
  );
}
